import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useClickOutside } from "@/hooks/useClickOutside";
import {
  EMAIL_MODAL_CHECKBOX_CHECKED_CLASS,
  EMAIL_MODAL_CHECKBOX_CLASS,
  EMAIL_MODAL_DROPDOWN_LIST_CLASS,
  EMAIL_MODAL_DROPDOWN_OPTION_CLASS,
  EMAIL_MODAL_DROPDOWN_PANEL_CLASS,
  EMAIL_MODAL_DROPDOWN_SEARCH_INPUT_CLASS,
  EMAIL_MODAL_DROPDOWN_SEARCH_ROW_CLASS,
  EMAIL_MODAL_FIELD_TRIGGER_CLASS,
  EMAIL_MODAL_FIELD_TRIGGER_OPEN_CLASS,
  EMAIL_MODAL_LABEL_CLASS,
} from "./styles";

export interface CheckboxDropdownOption {
  value: string;
  label: string;
  subtitle?: string;
  initials?: string;
}

interface SearchableCheckboxDropdownProps {
  label: string;
  placeholder: string;
  options: CheckboxDropdownOption[];
  value: string[];
  onChange: (value: string[]) => void;
  showAvatar?: boolean;
}

export const SearchableCheckboxDropdown: React.FC<
  SearchableCheckboxDropdownProps
> = ({ label, placeholder, options, value, onChange, showAvatar = false }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useClickOutside<HTMLDivElement>(
    () => setOpen(false),
    open,
  );

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return options;

    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(query) ||
        option.subtitle?.toLowerCase().includes(query),
    );
  }, [options, search]);

  const displayLabel = useMemo(() => {
    if (value.length === 0) return placeholder;

    const selected = options.filter((option) => value.includes(option.value));
    if (selected.length === 1) return selected[0].label;
    return `${selected.length} selected`;
  }, [options, placeholder, value]);

  const toggleOption = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((item) => item !== optionValue));
      return;
    }

    onChange([...value, optionValue]);
  };

  const handleToggle = () => {
    setOpen((prev) => {
      if (prev) setSearch("");
      return !prev;
    });
  };

  return (
    <div ref={containerRef} className="flex flex-col gap-1.5">
      <Label className={EMAIL_MODAL_LABEL_CLASS}>{label}</Label>

      <div className="relative flex flex-col">
        <button
          type="button"
          aria-expanded={open}
          onClick={handleToggle}
          className={cn(
            EMAIL_MODAL_FIELD_TRIGGER_CLASS,
            open && EMAIL_MODAL_FIELD_TRIGGER_OPEN_CLASS,
          )}
        >
          <span
            className={cn(
              "truncate font-normal",
              value.length === 0 ? "text-black/45" : "text-[#0A0A0A]",
            )}
          >
            {displayLabel}
          </span>
          <ChevronDown
            className={cn(
              "size-4 shrink-0 text-black/45 transition-transform duration-200",
              open && "rotate-180",
            )}
          />
        </button>

        {open ? (
          <div className={EMAIL_MODAL_DROPDOWN_PANEL_CLASS}>
            <div className={EMAIL_MODAL_DROPDOWN_SEARCH_ROW_CLASS}>
              <Search className="size-4 shrink-0 text-black/45" aria-hidden />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("analytics.common.search", "Search")}
                className={EMAIL_MODAL_DROPDOWN_SEARCH_INPUT_CLASS}
              />
            </div>

            <div className={EMAIL_MODAL_DROPDOWN_LIST_CLASS}>
              {filteredOptions.length === 0 ? (
                <p className="px-3 py-2.5 text-sm text-black/45">
                  {t("analytics.common.noResultsFound", "No results found")}
                </p>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = value.includes(option.value);

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => toggleOption(option.value)}
                      className={EMAIL_MODAL_DROPDOWN_OPTION_CLASS}
                    >
                      <span
                        className={cn(
                          EMAIL_MODAL_CHECKBOX_CLASS,
                          isSelected && EMAIL_MODAL_CHECKBOX_CHECKED_CLASS,
                        )}
                      >
                        {isSelected ? (
                          <Check className="size-3 stroke-[2.5]" />
                        ) : null}
                      </span>

                      {showAvatar ? (
                        <Avatar className="size-6 text-[10px]">
                          <AvatarFallback className="bg-zinc-200 text-zinc-700">
                            {option.initials}
                          </AvatarFallback>
                        </Avatar>
                      ) : null}

                      <span className="min-w-0 flex-1 truncate">
                        {option.label}
                        {option.subtitle ? (
                          <span className="text-black/45">
                            {" "}
                            — {option.subtitle}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
